import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './features/auth/AuthProvider'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ChatPage } from './pages/ChatPage'
import { ProfilePage } from './pages/ProfilePage'
import { SharedChatPage } from './pages/SharedChatPage'
import { Card } from './shared/ui/Card'
import { LoadingIndicator } from './shared/ui/LoadingIndicator'
import { PWA_UPDATE_EVENT } from './features/update/pwaUpdate'
import { DEFAULT_TERMINAL_CONFIG, loadTerminalConfig, type TerminalConfig } from './shared/terminalConfig'

const routes = [{ path: '/chat', label: '聊天' }, { path: '/profile', label: '个人资料' }]

const TerminalConfigContext = createContext<TerminalConfig>(DEFAULT_TERMINAL_CONFIG)
export function useTerminalConfig() { return useContext(TerminalConfigContext) }

function Page({ title, children }: { title: string; children: ReactNode }) {
  const config = useTerminalConfig()
  return <main className="page-shell"><header className="page-header"><Link className="brand" to="/">{config.brandName}</Link><nav aria-label="主导航">{routes.map((route) => <Link key={route.path} to={route.path}>{route.label}</Link>)}</nav></header><section className="page-content"><p className="eyebrow">ZHAOYOU / WEB</p><h1>{title}</h1>{children}</section></main>
}

function Placeholder({ title }: { title: string }) { return <Page title={title}><Card><p>此页面将在对应功能票中实现。</p></Card></Page> }

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { ready, session } = useAuth()
  const location = useLocation()
  if (!ready) return <main className="route-loading"><LoadingIndicator label="正在恢复登录状态…" /></main>
  return session ? <>{children}</> : <Navigate to="/login" replace state={{ from: location.pathname }} />
}

export function App() {
  const [config, setConfig] = useState(DEFAULT_TERMINAL_CONFIG)
  useEffect(() => { void loadTerminalConfig().then(setConfig).catch(() => undefined) }, [])
  useEffect(() => { if (config.primaryColor) document.documentElement.style.setProperty('--primary-color', config.primaryColor) }, [config.primaryColor])
  return <TerminalConfigContext.Provider value={config}><Routes><Route path="/" element={<Navigate to="/login" replace />} /><Route path="/login" element={<LoginPage />} /><Route path="/register" element={<RegisterPage />} /><Route path="/forgot-password" element={<ForgotPasswordPage />} /><Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} /><Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} /><Route path="/shared-chat/:token" element={<SharedChatPage />} /><Route path="*" element={<Placeholder title="页面不存在" />} /></Routes><UpdatePrompt /></TerminalConfigContext.Provider>
}

function UpdatePrompt() {
  const [activate, setActivate] = useState<null | (() => void)>(null)
  useEffect(() => { const ready = (event: Event) => setActivate(() => (event as CustomEvent<() => void>).detail); window.addEventListener(PWA_UPDATE_EVENT, ready); return () => window.removeEventListener(PWA_UPDATE_EVENT, ready) }, [])
  if (!activate) return null
  return <aside className="pwa-update-prompt" role="status"><div><strong>发现新版本</strong><small>更新后即可使用最新功能</small></div><button onClick={activate}>立即更新</button><button className="secondary" onClick={() => setActivate(null)}>稍后</button></aside>
}
